import { useTranslation } from 'react-i18next'
import { hasAtLeast, isEmpty } from 'remeda'
import { SimpleRelatedSolesContainer } from './SimpleRelatedSoles.styles'

/**
 * Creates a product link HTML string with accessibility attributes.
 */
const createSoleLink = (
  sole: { handle: string; title: string },
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  // Use the translation for aria-label, similar to how soleAlternative_one works
  /**
   * Remove any HTML tags for aria-label.
   */
  const ariaLabel = t('soleAlternative_one', { product: sole.title })
    .replaceAll('&lt;0&gt;&lt;/0&gt;', sole.title.toLowerCase())
    .replaceAll(/<[^>]*>/g, '')

  return `<a href="/products/${sole.handle}" aria-label="${ariaLabel}" rel="nofollow">${sole.title.toLowerCase()}</a>`
}

/**
 * Processes translation with multiple replacements.
 */
const processTranslation = (
  translation: string,
  replacements: Record<string, string>,
): string => {
  let processedText = translation

  for (const [placeholder, replacement] of Object.entries(replacements)) {
    processedText = processedText.replaceAll(placeholder, replacement)
  }

  return processedText
}

/**
 * Renders the final result with dangerouslySetInnerHTML and accessibility attributes.
 */
const renderResult = (processedText: string): JSX.Element => {
  return (
    <SimpleRelatedSolesContainer
      dangerouslySetInnerHTML={{ __html: processedText }}
      role="region"
      aria-live="polite"
      aria-atomic="true"
    />
  )
}

interface SimpleRelatedSolesProps {
  soles: { handle: string; title: string }[]
}

export const SimpleRelatedSoles = ({
  soles,
}: SimpleRelatedSolesProps): JSX.Element | null => {
  const { t } = useTranslation('product')

  if (isEmpty(soles)) {
    return null
  }

  if (soles.length === 1) {
    const sole = soles[0]
    const rawTranslation = t('soleAlternative_one')
    const link = createSoleLink(sole, t)

    const processedText = processTranslation(rawTranslation, {
      '&lt;0&gt;&lt;/0&gt;': link,
    })

    return renderResult(processedText)
  }

  if (soles.length === 2) {
    const [s1, s2] = soles
    const rawTranslation = t('soleAlternative_two')
    const link1 = createSoleLink(s1, t)
    const link2 = createSoleLink(s2, t)

    const processedText = processTranslation(rawTranslation, {
      '&lt;0&gt;&lt;/0&gt;': link1,
      '&lt;1&gt;&lt;/1&gt;': link2,
    })

    return renderResult(processedText)
  }

  if (hasAtLeast(soles, 3)) {
    const [firstSole, ...otherSoles] = soles
    const manyTranslation = t('soleAlternative_many')
    const joinerTranslation = t('soleAlternative_joiner')
    const firstLink = createSoleLink(firstSole, t)

    const otherSoleLinks = otherSoles
      .map((sole) => {
        const link = createSoleLink(sole, t)

        return processTranslation(joinerTranslation, {
          '&lt;0&gt;{{sole}}&lt;/0&gt;': link,
          '{{sole}}': sole.title.toLowerCase(),
        })
      })
      .join(', ')

    const processedText = processTranslation(manyTranslation, {
      '&lt;0&gt;{{sole1}}&lt;/0&gt;': firstLink,
      '{{sole1}}': firstSole.title.toLowerCase(),
      '{{otherSoles}}': otherSoleLinks,
    })

    return renderResult(processedText)
  }

  return null
}
